"use client";
import { cn } from "@layer/utils/cn";
import type { ReactNode } from "react";
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
	NestedDrawer,
} from "../../components/drawer";

type PageDrawerProps = {
	trigger: ReactNode;
	title?: ReactNode;
	description?: ReactNode;
	nested?: boolean;
	content?: ReactNode;
	width?: string;
	height?: string;
	modal?: boolean;
	direction?: "top" | "bottom" | "left" | "right" | undefined;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	dismissible?: boolean;
};
type DrawerRootProps = Partial<PageDrawerProps> & {
	children: ReactNode;
};

const DrawerRoot = ({ nested, children, ...restProps }: DrawerRootProps) => {
	return nested ? (
		<NestedDrawer {...restProps}>{children}</NestedDrawer>
	) : (
		<Drawer {...restProps}>{children} </Drawer>
	);
};

const PageDrawer = ({
	trigger,
	content,
	title,
	nested = false,
	description,
	width = "w-1/2",
	open,
	onOpenChange,
	dismissible = true,
}: PageDrawerProps) => {
	return (
		<DrawerRoot
			nested={nested}
			direction="right"
			modal={true}
			// If you pass in open or onOpenChange, the drawer will be controlled -> otherwise it will remain uncontrolled
			open={open}
			onOpenChange={onOpenChange}
			dismissible={dismissible}
		>
			<DrawerTrigger asChild>{trigger}</DrawerTrigger>
			<DrawerContent
				className={cn(
					"max-lg:w-1/2 max-md:w-3/4 max-sm:w-11/12",
					width,
				)}
				style={{ userSelect: "text" }}
			>
				{/* Only reason for only checking for title and not both title && descriptino is because if there's no title, we won't show the description.  */}
				<section
					className={cn(
						// Allows the Close button to be individually aligned on top.
						!title && "pt-8",
						"bg-neutral-surface flex max-h-full w-full grow flex-col overflow-clip rounded-3xl",
					)}
				>
					{title ? (
						<DrawerHeader
							className={cn(!description && "h-14", "border-b")}
						>
							<DrawerTitle>{title}</DrawerTitle>
							<DrawerDescription>{description}</DrawerDescription>
						</DrawerHeader>
					) : (
						<DrawerHeader>
							<DrawerTitle />
							<DrawerDescription />
						</DrawerHeader>
					)}
					<div className="h-[calc(100%-3.5rem)]">{content}</div>
				</section>
			</DrawerContent>
		</DrawerRoot>
	);
};

export default PageDrawer;
